import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = {
  closed: "#059669",
  open: "#3b82f6",
  in_progress: "#eab308",
  waiting: "#d97706",
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  
  // Calculate active workload from payload
  const openVal = payload.find(p => p.dataKey === 'open')?.value || 0;
  const inProgressVal = payload.find(p => p.dataKey === 'in_progress')?.value || 0;
  const activeWorkload = openVal + inProgressVal;
  
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xl text-sm">
      <p className="text-slate-800 font-bold mb-1.5">{label}</p>
      <div className="flex items-center gap-2 text-xs mb-1 pb-1 border-b">
        <span className="text-orange-600 font-semibold">Active Workload: {activeWorkload}</span>
      </div>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs mb-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-500">{entry.name}:</span>
          <span className="font-semibold text-slate-700">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function AssignedToChart({ data }) {
  const navigate = useNavigate();

  if (!data || data.length === 0) return null;

  const handleBarClick = (entry) => {
    if (entry?.name) {
      navigate(`/tickets?assigned_to=${encodeURIComponent(entry.name)}`);
    }
  };

  return (
    <div className="corp-card animate-fade-slide-up" style={{ animationDelay: "400ms" }} data-testid="assigned-to-chart">
      <div className="h-1 w-full bg-[#B22234]" />
      <div className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-bold text-slate-800">Tickets by Assignee</h3>
            <p className="text-xs text-slate-400 mt-0.5">Workload distribution (Open + In Progress = Active)</p>
          </div>
          <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-1 rounded-md font-medium">Click bar to filter</span>
        </div>

        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} onClick={(e) => e?.activePayload && handleBarClick(e.activePayload[0]?.payload)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="name"
              tick={{ fill: '#475569', fontSize: 11, fontFamily: 'DM Sans', fontWeight: 500 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
              interval={0}
              angle={-15}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontFamily: 'DM Sans', fontSize: 11, fontWeight: 500 }}
            />
            <Bar dataKey="closed" stackId="a" fill={COLORS.closed} name="Closed" radius={[0, 0, 0, 0]} />
            <Bar dataKey="open" stackId="a" fill={COLORS.open} name="Open" radius={[0, 0, 0, 0]} />
            <Bar dataKey="in_progress" stackId="a" fill={COLORS.in_progress} name="In Progress" radius={[0, 0, 0, 0]} />
            <Bar dataKey="waiting" stackId="a" fill={COLORS.waiting} name="Waiting" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        {/* Resource stats table */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="grid grid-cols-5 gap-2 text-[10px] font-bold text-slate-400 mb-3 px-2 uppercase tracking-wider">
            <span>Resource</span>
            <span className="text-center">Active</span>
            <span className="text-center">Open</span>
            <span className="text-center">In Prog</span>
            <span className="text-right">Avg Resolve</span>
          </div>
          {data.map((a, idx) => {
            const activeWorkload = (a.open || 0) + (a.in_progress || 0);
            return (
              <div
                key={a.name}
                className={`grid grid-cols-5 gap-2 text-sm py-2.5 px-2 rounded-lg cursor-pointer transition-colors ${idx % 2 === 0 ? 'bg-slate-50/50' : ''} hover:bg-slate-100`}
                onClick={() => navigate(`/tickets?assigned_to=${encodeURIComponent(a.name)}`)}
              >
                <span className="text-slate-700 font-semibold truncate">{a.name}</span>
                <span className="text-center">
                  <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-bold ${activeWorkload > 0 ? 'bg-orange-100 text-orange-700' : 'text-slate-400'}`}>
                    {activeWorkload}
                  </span>
                </span>
                <span className="text-center">
                  <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-bold ${a.open > 0 ? 'bg-blue-100 text-blue-700' : 'text-slate-400'}`}>
                    {a.open || 0}
                  </span>
                </span>
                <span className="text-center">
                  <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-bold ${a.in_progress > 0 ? 'bg-yellow-100 text-yellow-700' : 'text-slate-400'}`}>
                    {a.in_progress || 0}
                  </span>
                </span>
                <span className="text-right text-slate-500 text-xs">
                  {a.avg_resolve > 0 ? `${Math.round(a.avg_resolve / 60)}h` : '-'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
